/* eslint-disable no-shadow */
import { uniqBy } from 'lodash-es';

import {
  getProducts,
  saveProduct,
  saveProductToCollection,
  deleteProduct,
  getCollections,
  getSettingsByAffiliate,
} from '@/js/services/shoppable';

const state = {
  amazonTrackingId: null,

  products: [],
  productPage: 1,
  productLimit: 20,
  productSearch: '',
  productSortBy: 'createdAt',
  productSortDir: 'desc',
  productTotal: 0,
  isLoadingProducts: false,

  selectedCollection: null,

  collections: [],
  collectionPage: 1,
  collectionLimit: 20,
  collectionSearch: '',
  collectionSortBy: 'createdAt',
  collectionSortDir: 'desc',
  collectionTotal: 0,
  isLoadingCollections: false,

  showCatalogModal: false,
};

const getters = {
  hasMoreProducts: (state) => state.productTotal > (state.productPage * state.productLimit),
  hasMoreCollections: (state) => state.collectionTotal > (state.collectionPage * state.collectionLimit),

  selectedCollectionData: (state) => {
    if (!state.selectedCollection) {
      return null;
    }

    if (state.selectedCollection === -1) {
      return {
        id: -1,
        name: 'All Products',
      };
    }

    return state.collections.find((collection) => collection.id === state.selectedCollection);
  },
  isLoading: (state) => state.isLoadingProducts || state.isLoadingCollections,
};

const mutations = {
  /* eslint-disable no-param-reassign */
  ADD_PRODUCTS(state, products) {
    state.products = uniqBy(
      state.products.concat(products),
      'id',
    );
  },

  SET_PRODUCTS(state, products) {
    state.products = products;
  },

  ADD_COLLECTIONS(state, collections) {
    state.collections = uniqBy(
      state.collections.concat(collections),
      'id',
    );
  },

  SET_COLLECTIONS(state, collections) {
    state.collections = collections;
  },


  SET_STATE_FIELD(state, { key, value }) {
    state[key] = value;
  },

  SET_MODAL_STATE(state, value) {
    state.showCatalogModal = value;
  },
};

const actions = {
  async getSettingsByAffiliate({ rootState, commit }) {
    const { production } = rootState.production;
    const { owner } = production;

    const resp = await getSettingsByAffiliate(owner, 'amazon');
    const { trackingId } = resp.config;

    commit('SET_STATE_FIELD', { key: 'amazonTrackingId', value: trackingId });
  },

  async getProducts({ rootState, commit, state }, { reset = false, collectionId } = {}) {
    commit('SET_STATE_FIELD', { key: 'isLoadingProducts', value: true });

    if (reset) {
      commit('SET_STATE_FIELD', { key: 'productPage', value: 1 });
      commit('SET_PRODUCTS', []);
    }
    
    const { production } = rootState.production;
    const { owner } = production;


    const resp = await getProducts(owner, {
      page: state.productPage,
      limit: state.productLimit,
      search: state.productSearch,
      sortBy: state.productSortBy,
      sortDir: state.productSortDir,
      collectionId,
    });

    const { products, total } = resp;

    commit('SET_STATE_FIELD', { key: 'productTotal', value: total });
    commit('ADD_PRODUCTS', products);
    commit('SET_STATE_FIELD', { key: 'isLoadingProducts', value: false });
  },
  incrementProductPage({ commit, dispatch, state }) {
    commit('SET_STATE_FIELD', { key: 'productPage', value: state.productPage + 1 });
    dispatch('getProducts');
  },
  setProducts({ commit }, products) {
    commit('SET_PRODUCTS', products);
  },
  async saveProduct({ rootState, dispatch }, { source, sourceId, collectionId = null }) {
    const { production } = rootState.production;
    const { owner } = production;
    const hasCollection = collectionId && collectionId > 0;

    if (hasCollection) {
      await saveProductToCollection(owner, collectionId, {
        source,
        sourceId,
      });
    } else {
      await saveProduct(owner, {
        source,
        sourceId,
      });
    }
    
    await dispatch('getProducts', {
      reset: true,
      ...(hasCollection && { collectionId }),
    });
  },
  async deleteProduct({ rootState }, productId) {
    const { production } = rootState.production;
    const { owner } = production;

    await deleteProduct(owner, productId);
  },
  searchProducts({ commit, dispatch }, { search, collectionId }) {
    commit('SET_STATE_FIELD', { key: 'productSearch', value: search });
    dispatch('getProducts', { reset: true, collectionId });
  },
  resetProductSearch({ commit, dispatch, state }) {
    commit('SET_STATE_FIELD', { key: 'productSearch', value: '' });

    if (state.selectedCollection === -1) {
      dispatch('getProducts', { reset: true });
    } else {
      dispatch('getProducts', { reset: true, collectionId: state.selectedCollection });
    }
  },

  setSelectedCollection({ commit }, collection) {
    commit('SET_STATE_FIELD', { key: 'selectedCollection', value: collection });
  },
  async getCollections({ rootState, commit, state }, { reset = false } = {}) {
    commit('SET_STATE_FIELD', { key: 'isLoadingCollections', value: true });

    if (reset) {
      commit('SET_STATE_FIELD', { key: 'collectionPage', value: 1 });
      commit('SET_COLLECTIONS', []);
    }

    const { production } = rootState.production;
    const { owner } = production;

    const resp = await getCollections(owner, {
      page: state.collectionPage,
      limit: state.collectionLimit,
      search: state.collectionSearch,
      sortBy: state.collectionSortBy,
      sortDir: state.collectionSortDir,
    });

    const { collections, total } = resp;

    commit('SET_STATE_FIELD', { key: 'collectionTotal', value: total });
    commit('ADD_COLLECTIONS', collections);
    commit('SET_STATE_FIELD', { key: 'isLoadingCollections', value: false });
  },
  incrementCollectionPage({ commit, dispatch, state }) {
    commit('SET_STATE_FIELD', { key: 'collectionPage', value: state.collectionPage + 1 });
    dispatch('getCollections');
  },
  setCollections({ commit }, collections) {
    commit('SET_COLLECTIONS', collections);
  },
  searchCollections({ commit, dispatch }, search) {
    commit('SET_STATE_FIELD', { key: 'collectionSearch', value: search });
    dispatch('getCollections', { reset: true });
  },
  resetCollectionSearch({ commit, dispatch }) {
    commit('SET_STATE_FIELD', { key: 'collectionSearch', value: '' });
    dispatch('getCollections', { reset: true });
  },

  openCatalogModal({ commit }) {
    commit('SET_MODAL_STATE', true);
  },
  closeCatalogModal({ commit }) {
    commit('SET_MODAL_STATE', false);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
